import React, { useCallback, useEffect, useState } from 'react';
import LoaderButton from '../LoaderButton';
import OAuthAPI from '../../lib/OAuthAPI';

function messageFor(error) {
	return error && error.response && error.response.data && error.response.data.error_description
		? error.response.data.error_description
		: 'We could not sign you in. Check your details and try again.';
}

function OAuthSignIn({ onSignedIn, sport = 'rugby' }) {
	let [email, setEmail] = useState('');
	let [password, setPassword] = useState('');
	let [loading, setLoading] = useState(null);
	let [error, setError] = useState(null);
	let [csrfReady, setCsrfReady] = useState(false);
	let [providers, setProviders] = useState([]);

	let refreshSession = useCallback(async () => {
		const session = await OAuthAPI.getSession({ sport });
		setProviders(session.providers || []);
		return session;
	}, [sport]);

	useEffect(() => {
		// Establish a short-lived double-submit CSRF token before any login
		// mutation. This endpoint never returns an authenticated session to a
		// third party; it is served only from auth.blackout.games.
		setProviders([]);
		refreshSession().then(() => setCsrfReady(true)).catch(() => undefined);
	}, [refreshSession]);

	let finish = async data => {
		setError(null);
		try {
			if(!OAuthAPI.getCsrf()) await refreshSession();
			await OAuthAPI.login(data);
			onSignedIn();
		} catch (requestError) {
			// Login CSRF tokens are single-use. Refresh the short-lived token after
			// any failed attempt so a typo does not force the manager to reload.
			try { await refreshSession(); } catch (_csrfError) { /* Show the original sign-in error. */ }
			setError(messageFor(requestError));
		} finally {
			setLoading(null);
		}
	};

	let passwordLogin = event => {
		event.preventDefault();
		setLoading('password');
		finish({ type: 'password', email, password });
	};

	let socialLogin = async provider => {
		setLoading(provider);
		setError(null);
		try {
			if(!OAuthAPI.getCsrf()) await refreshSession();
			const result = await OAuthAPI.startProviderLogin({
				provider,
				sport,
				return_to: `${window.location.pathname}${window.location.search}`,
			});
			// The provider assertion is posted to the Blackout-owned callback and
			// verified server-side. This page sees only a provider authorization URL.
			window.location.assign(result.authorization_url);
		} catch (providerError) {
			try { await refreshSession(); } catch (_csrfError) { /* Preserve the provider error. */ }
			setLoading(null);
			setError(messageFor(providerError));
		}
	};

	return (
		<div className="oauth-sign-in">
			<h2>Sign in to Blackout</h2>
			<p>Your password and any social-provider assertion are submitted only to Blackout. They are never shared with the app requesting access.</p>
			<form onSubmit={passwordLogin}>
				<label>Email<input type="email" autoComplete="username" value={email} onChange={event => setEmail(event.target.value)} required /></label>
				<label>Password<input type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required /></label>
				<LoaderButton className="loader-button" isLoading={loading === 'password'} disabled={Boolean(loading) || !csrfReady} isSubmitButton>Sign in</LoaderButton>
			</form>
			{providers.length ? <div className="oauth-provider-buttons">
				{providers.includes('facebook') ? <LoaderButton className="fb-button" isLoading={loading === 'facebook'} disabled={Boolean(loading) || !csrfReady} onClick={() => socialLogin('facebook')}>Continue with Facebook</LoaderButton> : null}
				{providers.includes('apple') ? <LoaderButton className="apple-button" isLoading={loading === 'apple'} disabled={Boolean(loading) || !csrfReady} onClick={() => socialLogin('apple')}>Continue with Apple</LoaderButton> : null}
				{providers.includes('google') ? <LoaderButton className="google-button" isLoading={loading === 'google'} disabled={Boolean(loading) || !csrfReady} onClick={() => socialLogin('google')}>Continue with Google</LoaderButton> : null}
			</div> : null}
			{error ? <p className="validation-text">{error}</p> : null}
		</div>
	);
}

export default OAuthSignIn;
